import { cn } from "@/lib/utils";
import { FIELD_LANE_DOT } from "@shared/fieldCalendarColors";
import { logFieldCalendarCrash } from "./fieldCalendarDiagnostics";
import {
  coerceValidDay,
  dateKey,
  getMonthGridDays,
  getVisitLane,
  getWeekDays,
  isSameDay,
  isValidDate,
  leaveBlocksForDate,
  sanitizeVisit,
  startOfMonth,
  visitDisplayLabel,
} from "./fieldCalendarUtils";
import { blockClassForLeave, blockClassForVisit } from "./visitBlockStyles";
import type { CalendarLeaveBlock, CalendarVisit } from "./fieldCalendarTypes";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type Props = {
  viewMode: "month" | "week";
  anchorDate: Date;
  selectedDay: Date;
  visitsByDate: Record<string, CalendarVisit[]>;
  leaveBlocks: CalendarLeaveBlock[] | null | undefined;
  today: Date;
  myScheduleUserId?: number;
  onSelectDay: (day: Date) => void;
};

function dayLaneDots(visits: CalendarVisit[] | undefined, max = 6): string[] {
  const lanes: string[] = [];
  const list = Array.isArray(visits) ? visits : [];
  for (const raw of list) {
    const v = sanitizeVisit(raw);
    if (!v) continue;
    const lane = getVisitLane(v);
    const dot = FIELD_LANE_DOT[lane] ?? FIELD_LANE_DOT.others ?? "bg-slate-500";
    if (!lanes.includes(dot)) lanes.push(dot);
    if (lanes.length >= max) break;
  }
  return lanes;
}

export function FieldCalendarMonthWeek({
  viewMode,
  anchorDate,
  selectedDay,
  visitsByDate,
  leaveBlocks,
  today,
  myScheduleUserId,
  onSelectDay,
}: Props) {
  const safeAnchor = coerceValidDay(anchorDate);
  const safeSelected = coerceValidDay(selectedDay);
  const safeToday = coerceValidDay(today);

  let days: Date[] = [];
  try {
    days =
      viewMode === "month" ? getMonthGridDays(safeAnchor) : getWeekDays(safeAnchor);
    days = days.filter(isValidDate);
  } catch (error) {
    logFieldCalendarCrash("monthWeek.gridDays", error, { viewMode });
    days = [safeToday];
  }

  const monthStart = startOfMonth(safeAnchor);
  const safeVisitsByDate = visitsByDate && typeof visitsByDate === "object" ? visitsByDate : {};

  return (
    <div className="bg-white" style={{ minHeight: viewMode === "month" ? 380 : 200 }}>
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="py-1 text-center text-[10px] font-bold uppercase tracking-tight text-slate-600"
          >
            {w}
          </div>
        ))}
      </div>
      <div
        className={cn(
          "grid grid-cols-7 gap-px bg-slate-200",
          viewMode === "month" ? "grid-rows-6" : "grid-rows-1",
        )}
      >
        {days.map((day) => {
          const key = dateKey(day);
          const dayVisits = Array.isArray(safeVisitsByDate[key])
            ? safeVisitsByDate[key]!
            : [];
          const dayLeave = leaveBlocksForDate(leaveBlocks, day, myScheduleUserId);
          const inMonth =
            viewMode === "month" ? day.getMonth() === monthStart.getMonth() : true;
          const isToday = isSameDay(day, safeToday);
          const isSelected = isSameDay(day, safeSelected);
          const dots = dayLaneDots(dayVisits);
          const total = dayVisits.length + dayLeave.length;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDay(day)}
              className={cn(
                "flex min-h-[64px] flex-col bg-white p-1.5 text-left transition-colors sm:min-h-[72px]",
                isSelected && "ring-2 ring-inset ring-blue-600",
                !isSelected && "active:bg-slate-50",
                !inMonth && viewMode === "month" && "bg-slate-50/80",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                    isToday && "bg-blue-600 text-white",
                    !isToday && "text-slate-800",
                  )}
                >
                  {day.getDate()}
                </span>
                {total > 0 && (
                  <span className="text-[10px] font-bold text-slate-500">{total}</span>
                )}
              </div>

              {viewMode === "month" ? (
                <div className="mt-1 flex flex-1 flex-col justify-end gap-0.5">
                  {dayLeave.length > 0 && (
                    <span
                      className={cn(
                        "inline-block w-fit rounded px-1 py-px text-[9px] font-bold",
                        blockClassForLeave("chip"),
                      )}
                    >
                      Leave
                    </span>
                  )}
                  <div className="flex flex-wrap justify-center gap-0.5">
                    {dots.map((dot, i) => (
                      <span key={`${key}-dot-${i}`} className={cn("h-2 w-2 rounded-full", dot)} />
                    ))}
                  </div>
                  {dayVisits.length > 0 && (
                    <>
                      <p className="truncate text-center text-[9px] font-bold text-slate-700">
                        {dayVisits.length} appt{dayVisits.length === 1 ? "" : "s"}
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="mt-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                  {dayLeave.slice(0, 1).map((lb) => (
                    <span
                      key={lb.id ?? `leave-${key}`}
                      className={cn(
                        "rounded px-1.5 py-0.5 text-[10px] font-bold",
                        blockClassForLeave("chip"),
                      )}
                    >
                      Leave
                    </span>
                  ))}
                  {dayVisits.slice(0, 5).map((raw) => {
                    const v = sanitizeVisit(raw);
                    if (!v) return null;
                    const lane = getVisitLane(v);
                    return (
                      <span
                        key={v.id}
                        className={cn(
                          "truncate rounded px-1.5 py-1 text-[10px] font-semibold leading-tight",
                          blockClassForVisit(lane, undefined, "chip"),
                        )}
                      >
                        {visitDisplayLabel(v)}
                      </span>
                    );
                  })}
                  {dayVisits.length > 5 && (
                    <span className="text-[10px] font-bold text-slate-500">
                      +{dayVisits.length - 5} more
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
